/**
 * Helpers puros da Ordem de Entrega avulsa (testados em goodsDeliveryOrderUtils.test.ts).
 * Sem React/serviços. A validação espelha as regras do backend
 * (validateGoodsDeliveryOrder no ERPApi) para o usuário ver o erro no campo
 * antes de levar um 400.
 */
import type {
  GoodsDeliveryOrder,
  GoodsDeliveryOrderItem,
  GoodsDeliveryOrderItemPayload,
  GoodsDeliveryOrderPayload,
  GoodsDeliveryOrderStatus,
} from '@/types/goodsDeliveryOrder';

// ─── Status ──────────────────────────────────────────────────────

/**
 * Status exibido. Cancelada e entregue são finais e têm precedência; "Em rota"
 * = pendente com parada ainda não entregue em alguma rota (`in_route`).
 */
export const deliveryOrderStatus = (
  order: Pick<GoodsDeliveryOrder, 'is_canceled' | 'is_delivered' | 'in_route'>,
): GoodsDeliveryOrderStatus => {
  if (order.is_canceled) return 'canceled';
  if (order.is_delivered) return 'delivered';
  if (order.in_route) return 'in_route';
  return 'pending';
};

export const DELIVERY_ORDER_STATUS_LABELS: Record<GoodsDeliveryOrderStatus, string> = {
  pending: 'Pendente',
  in_route: 'Em rota',
  delivered: 'Entregue',
  canceled: 'Cancelada',
};

export type StatusBadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

export const deliveryOrderStatusBadge = (status: GoodsDeliveryOrderStatus): StatusBadgeVariant => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_route':
      return 'info';
    case 'delivered':
      return 'success';
    case 'canceled':
      return 'neutral';
  }
};

/** Editar/cancelar só enquanto não entregue nem cancelada (o backend responde 409). */
export const isDeliveryOrderOpen = (order: Pick<GoodsDeliveryOrder, 'is_canceled' | 'is_delivered'>): boolean =>
  !order.is_canceled && !order.is_delivered;

/** Excluir: o backend só recusa ordem entregue (409). */
export const canDeleteDeliveryOrder = (order: Pick<GoodsDeliveryOrder, 'is_delivered'>): boolean =>
  !order.is_delivered;

// ─── Filtro "Somente emergência" via URL ─────────────────────────

/** `?emergency=1` (ou `true`) liga o filtro — usado pelo link do sino. */
export const parseEmergencyQuery = (search: string): boolean => {
  const value = new URLSearchParams(search).get('emergency');
  return value === '1' || value === 'true';
};

// ─── Cancelamento ────────────────────────────────────────────────

export const CANCEL_REASON_MAX = 500;

/** Motivo obrigatório (não vazio) e com no máximo 500 caracteres. */
export const validateCancelReason = (reason: string): string | null => {
  const trimmed = reason.trim();
  if (!trimmed) return 'Informe o motivo do cancelamento';
  if (trimmed.length > CANCEL_REASON_MAX) return `O motivo deve ter no máximo ${CANCEL_REASON_MAX} caracteres`;
  return null;
};

// ─── Quantidade (vírgula decimal) ────────────────────────────────

/**
 * Converte a quantidade digitada (pt-BR: "1,5", "1.234,5"; também aceita "1.5")
 * em número. Devolve `null` quando inválida, não positiva ou com mais de 3 casas
 * decimais ([Financial].[GoodsDeliveryOrdersItem].[Quantity] é decimal(18,3)).
 */
export const parseQuantity = (raw: string): number | null => {
  let text = raw.trim().replace(/\s/g, '');
  if (!text) return null;
  if (text.includes(',')) {
    // Vírgula é o separador decimal; pontos são milhar.
    text = text.replace(/\./g, '').replace(',', '.');
  }
  if (!/^\d+(\.\d{1,3})?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/** Quantidade para exibição/edição: vírgula decimal, até 3 casas, sem milhar. */
export const formatQuantity = (value: number): string =>
  value.toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false });

// ─── Itens do formulário ─────────────────────────────────────────

export interface DeliveryItemRow {
  description: string;
  /** Texto digitado (vírgula decimal). */
  quantity: string;
  unit: string;
}

export const emptyItemRow = (): DeliveryItemRow => ({ description: '', quantity: '1', unit: '' });

const isBlankRow = (row: DeliveryItemRow) =>
  !row.description.trim() && !row.quantity.trim() && !row.unit.trim();

/** Linhas do formulário a partir dos itens gravados. */
export const itemRowsFromOrder = (items: GoodsDeliveryOrderItem[] | undefined): DeliveryItemRow[] => {
  const rows = (items ?? []).map((i) => ({
    description: i.description ?? '',
    quantity: formatQuantity(i.quantity),
    unit: i.unit ?? '',
  }));
  return rows.length > 0 ? rows : [emptyItemRow()];
};

/**
 * Monta o payload de itens: ignora linhas totalmente vazias, apara textos e omite
 * a unidade em branco. Linhas inválidas são barradas antes por `validateDeliveryOrderForm`.
 */
export const buildItemsPayload = (rows: DeliveryItemRow[]): GoodsDeliveryOrderItemPayload[] =>
  rows
    .filter((row) => !isBlankRow(row))
    .map((row) => {
      const unit = row.unit.trim();
      return {
        description: row.description.trim(),
        quantity: parseQuantity(row.quantity) ?? 0,
        ...(unit ? { unit } : {}),
      };
    });

// ─── Formulário ──────────────────────────────────────────────────

export interface DeliveryOrderFormState {
  description: string;
  recipientName: string;
  recipientPhone: string;
  volumeQuantity: string;
  /** ISO "YYYY-MM-DD" (DateInputBR). */
  expectedDeliveryDate: string;
  isEmergency: boolean;
  observation: string;
  zip: string;
  street: string;
  num: string;
  compl: string;
  district: string;
  city: string;
  state: string;
  items: DeliveryItemRow[];
}

const digits = (value: string) => value.replace(/\D/g, '');

const isValidIsoDate = (iso: string): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  const daysInMonth = new Date(y, mo, 0).getDate();
  return d <= daysInMonth;
};

/** Chaves de erro de item: `items.<idx>.<campo>`. */
export const itemErrorKey = (idx: number, field: keyof DeliveryItemRow) => `items.${idx}.${field}`;

/**
 * Valida o formulário com as mesmas regras do backend. Devolve um mapa
 * campo → mensagem (vazio = válido). A ordem das chaves segue a do formulário.
 */
export const validateDeliveryOrderForm = (form: DeliveryOrderFormState): Record<string, string> => {
  const errors: Record<string, string> = {};
  const req = (key: string, value: string, msg: string, max?: number, maxMsg?: string) => {
    const v = value.trim();
    if (!v) errors[key] = msg;
    else if (max !== undefined && v.length > max) errors[key] = maxMsg ?? `Máximo de ${max} caracteres`;
  };
  const opt = (key: string, value: string, max: number) => {
    if (value.trim().length > max) errors[key] = `Máximo de ${max} caracteres`;
  };

  req('description', form.description, 'A descrição é obrigatória', 500);
  req('recipientName', form.recipientName, 'O destinatário é obrigatório', 100);
  opt('recipientPhone', form.recipientPhone, 20);

  const vol = form.volumeQuantity.trim();
  if (vol && (!/^\d+$/.test(vol) || Number(vol) < 1)) {
    errors.volumeQuantity = 'Informe um número inteiro a partir de 1';
  }

  if (!form.expectedDeliveryDate) errors.expectedDeliveryDate = 'A data prevista é obrigatória';
  else if (!isValidIsoDate(form.expectedDeliveryDate)) errors.expectedDeliveryDate = 'Data inválida';

  opt('observation', form.observation, 1000);

  const zip = digits(form.zip);
  if (!zip) errors.zip = 'O CEP é obrigatório';
  else if (zip.length !== 8) errors.zip = 'O CEP deve ter 8 dígitos';
  req('street', form.street, 'A rua é obrigatória', 100);
  req('num', form.num, 'O número é obrigatório', 10);
  opt('compl', form.compl, 100);
  req('district', form.district, 'O bairro é obrigatório', 100);
  req('city', form.city, 'A cidade é obrigatória', 100);
  const uf = form.state.trim();
  if (!uf) errors.state = 'Selecione o estado';
  else if (!/^[A-Za-z]{2}$/.test(uf)) errors.state = 'UF deve ter 2 letras';

  const filled = form.items.map((row, idx) => ({ row, idx })).filter(({ row }) => !isBlankRow(row));
  if (filled.length === 0) {
    errors.items = 'Adicione ao menos um item';
  }
  for (const { row, idx } of filled) {
    const desc = row.description.trim();
    if (!desc) errors[itemErrorKey(idx, 'description')] = 'Descrição obrigatória';
    else if (desc.length > 200) errors[itemErrorKey(idx, 'description')] = 'Máximo de 200 caracteres';
    if (parseQuantity(row.quantity) === null) {
      errors[itemErrorKey(idx, 'quantity')] = 'Quantidade maior que zero (até 3 casas)';
    }
    if (row.unit.trim().length > 10) errors[itemErrorKey(idx, 'unit')] = 'Máximo de 10 caracteres';
  }

  return errors;
};

/**
 * Monta o corpo do POST/PUT. Na edição (`clearBlankOptionals`), telefone e
 * observação em branco vão como "" para o backend apagar o valor anterior; na
 * criação são omitidos.
 */
export const buildDeliveryOrderPayload = (
  form: DeliveryOrderFormState,
  { clearBlankOptionals = false }: { clearBlankOptionals?: boolean } = {},
): GoodsDeliveryOrderPayload => {
  const optional = (value: string) => {
    const v = value.trim();
    if (v) return v;
    return clearBlankOptionals ? '' : undefined;
  };
  const vol = form.volumeQuantity.trim();
  const payload: GoodsDeliveryOrderPayload = {
    description: form.description.trim(),
    recipient_name: form.recipientName.trim(),
    ship_addr_street: form.street.trim(),
    ship_addr_num: form.num.trim(),
    ship_addr_compl: form.compl.trim(),
    ship_addr_district: form.district.trim(),
    ship_addr_city: form.city.trim(),
    ship_addr_state: form.state.trim().toUpperCase(),
    ship_addr_zip: digits(form.zip),
    expected_delivery_date: form.expectedDeliveryDate,
    is_emergency: form.isEmergency,
    items: buildItemsPayload(form.items),
  };
  const phone = optional(form.recipientPhone);
  if (phone !== undefined) payload.recipient_phone = phone;
  const observation = optional(form.observation);
  if (observation !== undefined) payload.observation = observation;
  if (vol) payload.volume_quantity = Number(vol);
  return payload;
};

/** "YYYY-MM-DD HH:MM:SS" (ou RFC3339) → "YYYY-MM-DD" para o DateInputBR. */
export const toDateInputValue = (value: string | null | undefined): string => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value ?? '');
  return m ? m[1] : '';
};
